import type { GenericError, RequestStatus } from '@skynet/data-ws-core';
import { LoanCommands } from '@skynet/loans';

export interface LoanTermsState {
  id?: number;
  details?: LoanTerms;
  requestStatus: RequestStatus;
  resUpdateLoan: RequestStatus;
  /**
   * The server-confirmed `id` and `details` from before the update in flight merged its payload
   * into `details` — which it does optimistically, before the server acks it. Restored if the
   * update is refused or its ack is lost, so the form falls back to what the server kept rather
   * than the refused values; `undefined` while no update is in flight.
   */
  confirmedTerms?: { id?: number; details?: LoanTerms };
  error: null | GenericError;
  isLoanEditing: boolean;
}

export enum LoanTermsCommands {
  Overview = 'Overview',
  LoanLiquidity = LoanCommands.LoanLiquidity,
  Terms = 'Terms',
  UpdateLoan = LoanCommands.UpdateLoan,
}

export interface AddLoanPayload {
  company: string; // Company name
  name: string; // Loan name
  started: string; // string in YYYY-MM-DD format
  ended: string; // string in YYYY-MM-DD format
  paybackAmounts: {
    baseAmount: number;
    quoteAmount: number;
    residualAmount?: number; // optional
  };
  exchanges: number[]; // list of exchanges ids
  pairs: string[]; // array of supported pairs, like TOKEN/USDT, TOKEN/BTC, TOKEN/BUSD
  tokens: string[]; // Token name
  loanType?: number; // LoanTypes value selected when creating the loan
  showAsRetainer: boolean; // whether the loan should be shown in Retainers domain
  autoSyncPairs: boolean; // when true, pairs/tokens are computed dynamically from active accounts; read-only on the UI
  // CoinGecko project id (e.g. `bitcoin`), the only editable CoinGecko value. Optional both
  // ways: a multi-token loan is created without one, and loans stored before SKY-4764 come
  // back without the key — treat a missing value as an empty id.
  coinGeckoProject?: string;
}

// `autoSyncPairs` is optional on the read side only: loans stored before SKY-4553
// come back without the key. Treat a missing value as `false`.
export interface JsonLoanTerms extends Omit<AddLoanPayload, 'autoSyncPairs'> {
  autoSyncPairs?: boolean;
  // Read-only and never sent: the backend computes it from `coinGeckoProject`. Always
  // present — an empty string when there is no project to derive it from.
  coinGeckoLink: string;
  initFunds: number; // used for a new loan form only
  targetDelta?: number;
  loanType: number;
  initialSellPercent: number;
  tokenPower: number; // numeric token power value associated with the loan
  splitOverview: boolean;
}

export type LoanTerms = JsonLoanTerms;

export interface JsonLoanTermsSuccess {
  command: LoanTermsCommands.Terms;
  details: JsonLoanTerms;
  id: number;
}
